import { ALL_FIELD_KEYS, FieldKey } from '../config/documentFieldSources';
import { ExtractedFieldEntry } from '../store/useFormCGIAStore';
import { isVinFormatValid } from '../validation/vinChecksum';

/**
 * Convertit la map `extractedFields` du store en :
 *  - `defaultValues` : valeurs par défaut à passer à React Hook Form
 *  - `confidences`   : confiance associée à chaque champ (utilisée par les
 *    wrappers `PrefilledTextField` pour colorer les bordures et badges).
 *
 * Mapping des clés OCR brutes (extraites par les prompts) vers les clés
 * de formulaire de FormCGIA (définies dans `documentFieldSources.ts`).
 */

type ExtractedMap = Record<string, ExtractedFieldEntry>;

/**
 * Mapping : clé du formulaire → liste des clés OCR brutes possibles
 * (dans l'ordre de priorité si plusieurs docs fournissent la même info).
 */
const FORM_TO_OCR_KEYS: Record<FieldKey, string[]> = {
  // Véhicule
  immatriculation: ['immatriculation'],
  vin: ['vin'],
  numero_formule: ['numero_formule'],
  date_premiere_mec: ['date_premiere_mec'],
  marque: ['marque'],
  genre: ['genre'],
  energie: ['energie'],
  puissance_cv: ['puissance_cv'],
  date_vente: ['date_vente'],
  // Repli sur l'heure manuscrite de la carte grise (« Vendu le … à 16h15 »). Sans lui,
  // une heure absente du Cerfa de cession mais bien écrite sur la CG était perdue et
  // l'heure de vente tombait à 00:00, premier motif de désaccord en relecture.
  // ⚠️ PAS de repli équivalent sur la date : le prompt signale que cette mention est
  // manuscrite sur une carte barrée, avec des confusions fréquentes de chiffres
  // (5↔1, 3↔1, 7↔1, 6↔8) sur le jour et le mois. Une date de vente absente est
  // corrigeable par le relecteur ; une date fausse d'apparence plausible ne l'est pas.
  // L'heure, elle, n'a pas ce risque : sans repli elle vaut 00:00, donc fausse à coup sûr.
  heure_vente: ['heure_vente', 'heure_cession_mention'],

  // Vendeur
  seller_client_type: [],
  seller_civilite: ['titulaire_civilite', 'vendeur_civilite'],
  seller_nom: ['titulaire_nom', 'vendeur_nom'],
  seller_prenom: ['titulaire_prenom', 'vendeur_prenom'],
  seller_nom_usage: ['titulaire_nom_usage'],
  // `vendeur_siren` / `vendeur_siret` / `vendeur_raison_sociale` peuvent aussi
  // venir du Kbis du VENDEUR, renommés à la fusion (KBIS_VENDEUR_FIELD_REMAP).
  seller_siren: ['titulaire_siren', 'vendeur_siren'],
  // SIRET vendeur = identifiant SIV/ANTS pour une personne morale (14 chiffres,
  // depuis la cession). Le SIREN (9) du C.1 CG ne suffit pas.
  seller_siret: ['vendeur_siret'],
  // ⚠️ PAS de repli sur `raison_sociale` : cette clé est celle du **Kbis**, et
  // dans un dossier CTVO le Kbis est celui de l'ACQUÉREUR personne morale (le
  // slot n'est matérialisé que quand `acheteurEstPersonneMorale`, et les
  // `kbisUrls` partent dans `buyerFilesStructured`). Avec ce repli, un dossier
  // dont la cession ne portait pas de raison sociale vendeur récupérait celle
  // de l'acheteur → `sellerIsPro` passait à true plus bas → le vendeur était
  // écrit `EntityType='pro'` avec la raison sociale de l'acheteur et un SIRET
  // vide, ce qui rendait le SIRET vendeur obligatoire dans la modale
  // « Soumettre le CTVO » et grisait le bouton (EH-214-VH, retour François
  // 2026-08-06 : « il indique en vendeur pro boyer-legrix alors que c'est
  // l'acheteur »). L'OCR de la cession disait pourtant
  // `vendeur_est_personne_morale: false` à 0.95.
  // Un vendeur personne morale est identifié par ses PROPRES champs :
  // `vendeur_raison_sociale` / `vendeur_siret` (cession) ou `titulaire_siren` (C.1 CG).
  seller_raison_sociale: ['vendeur_raison_sociale'],

  // Nouveau titulaire (acheteur). L'identité vient d'abord de la pièce
  // d'identité (`nom`/`prenom`/`civilite`), avec la cession (`acheteur_*`) en
  // FALLBACK : si aucune PI n'a été lue, le titulaire est au moins renseigné
  // depuis le document qui le désigne (retour François #2 — le titulaire est
  // désigné par la cession / le mandat / le 13750). NB : ce fallback ne
  // départage PAS deux CNI (titulaire vs cotitulaire) : ce cas relève de la
  // logique d'ingestion (pick de la bonne PI d'acquéreur), traité à part.
  buyer_client_type: [],
  buyer_civilite: ['civilite', 'acheteur_civilite'],
  buyer_nom: ['nom', 'acheteur_nom'],
  buyer_prenom: ['prenom', 'acheteur_prenom'],
  buyer_nom_usage: ['nom_usage'],
  buyer_date_naissance: ['date_naissance'],
  buyer_lieu_naissance: ['lieu_naissance'],
  buyer_departement_naissance: ['departement_naissance'],
  buyer_pays_naissance: ['pays_naissance'],
  // PM acheteur : mêmes clés et même ordre que l'affichage partagé vérif agence /
  // review (ACHETEUR_FIELDS) — SIRET : Kbis (`siret`) d'abord, cession
  // (`acheteur_siret`) en repli ; raison sociale : cession d'abord, Kbis en repli.
  // Sans ces replis, une valeur visible à l'écran n'arrivait pas dans le payload
  // de création.
  buyer_siret: ['siret', 'acheteur_siret', 'siren'],
  buyer_raison_sociale: ['acheteur_raison_sociale', 'raison_sociale'],
  buyer_numero_voie: ['numero_voie'],
  buyer_extension_voie: ['extension_voie'],
  buyer_type_voie: ['type_voie'],
  buyer_nom_voie: ['nom_voie'],
  buyer_lieu_dit: ['lieu_dit'],
  buyer_etage_escalier: ['etage_escalier', 'escalier', 'appartement'],
  buyer_complement_adresse: ['complement_adresse'],
  buyer_boite_postale: ['boite_postale'],
  buyer_code_postal: ['code_postal'],
  buyer_ville: ['ville'],
  buyer_pays: ['pays'],
  buyer_telephone: ['telephone'],
  buyer_residence_type: [],

  // Co-titulaire
  cobuyer_present: ['cotitulaire_present'],
  cobuyer_civilite: ['cotitulaire_civilite'],
  cobuyer_nom: ['cotitulaire_nom'],
  cobuyer_prenom: ['cotitulaire_prenom'],
  cobuyer_nom_usage: ['cotitulaire_nom_usage'],
  cobuyer_client_type: [],
  cobuyer_siret: [],
  cobuyer_raison_sociale: [],
};

export interface FormCgiaValues {
  values: Partial<Record<FieldKey, string | number | boolean | null>>;
  confidences: Partial<Record<FieldKey, number>>;
}

export const mergeExtractedFieldsToFormValues = (extractedFields: ExtractedMap): FormCgiaValues => {
  const values: Partial<Record<FieldKey, string | number | boolean | null>> = {};
  const confidences: Partial<Record<FieldKey, number>> = {};

  ALL_FIELD_KEYS.forEach((formKey) => {
    const candidateKeys = FORM_TO_OCR_KEYS[formKey] ?? [];
    for (const ocrKey of candidateKeys) {
      const entry = extractedFields[ocrKey];
      if (entry && entry.field.value !== null) {
        values[formKey] = entry.field.value as string | number | boolean;
        confidences[formKey] = entry.field.confidence;
        break;
      }
    }
    if (values[formKey] === undefined) {
      values[formKey] = '';
      confidences[formKey] = 0;
    }
  });

  // ─── Post-processing : normalisation des valeurs OCR ──────────────────

  // La mention manuscrite de la CG écrit l'heure « 16h15 » ; le champ datetime-local
  // exige « 16:15 ».
  //
  // ⚠️ Les bornes 0-23 / 0-59 sont indispensables, pas cosmétiques. Sans elles, une
  // mention manuscrite « 19h75 » traversait tout le parcours : la regex l'acceptait,
  // date_vente devenait « 2026-09-16T19:75 », le champ datetime-local refusait
  // d'afficher une valeur hors normes (donc l'agence ne voyait RIEN à corriger) et
  // l'INSERT final partait en 500 brut côté MySQL — ER_TRUNCATED_WRONG_VALUE sur
  // CG_Registrations.PurchaseDateTime. Dossier impossible à créer, en boucle
  // (cf. FK-371-EZ le 16/09 : huit soumissions consécutives, aucune ligne en base).
  const normalizeHeure = (raw: unknown): string => {
    // Les secondes sont tolérées puis jetées : le prompt OCR demande « HH:MM », mais
    // un « 14:00:00 » traversait jusqu'ici en l'état (MySQL l'acceptait). Les refuser
    // maintenant retomberait à tort sur 00:00.
    const match = /^(\d{1,2})\s*[h:]\s*(\d{1,2})?(?::\d{1,2})?$/i.exec(String(raw ?? '').trim());
    if (!match) return '';
    const heures = Number(match[1]);
    const minutes = Number(match[2] ?? '0');
    if (heures > 23 || minutes > 59) return '';
    return `${String(heures).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };
  if (values.heure_vente) {
    const heureNormalisee = normalizeHeure(values.heure_vente);
    // Une heure qu'on ne sait pas normaliser doit être JETÉE, pas conservée telle
    // quelle : le raw était concaténé dans date_vente juste en dessous, ce qui
    // produisait « 2026-09-16T19h75 », tout aussi irrécupérable. On retombe sur le
    // repli 00:00 + confiance 0 ci-dessous, seul état que le relecteur peut voir et
    // corriger.
    values.heure_vente = heureNormalisee;
    if (!heureNormalisee) confidences.heure_vente = 0;
  }

  // Merge date + heure → datetime-local (YYYY-MM-DDTHH:mm)
  const dateVente = values.date_vente as string | undefined;
  const heureVente = values.heure_vente as string | undefined;
  if (dateVente && heureVente && !dateVente.includes('T')) {
    values.date_vente = `${dateVente}T${heureVente}`;
  } else if (dateVente && !dateVente.includes('T')) {
    // 00:00 est un défaut affiché tel quel au relecteur, qui le prend pour une heure lue.
    // On le garde faute de mieux, mais la confiance tombe à 0 pour que le champ ressorte
    // comme non renseigné plutôt que comme une valeur extraite.
    values.date_vente = `${dateVente}T00:00`;
    confidences.heure_vente = 0;
  }

  // VIN : la norme ISO 3779 exclut I, O et Q parce qu'ils se confondent avec 1 et 0
  // (cf. VIN_FORMAT_REGEX, /^[A-HJ-NPR-Z0-9]{17}$/). Ces lettres ne peuvent donc jamais
  // être légitimes : quand l'OCR en rend une, c'est le chiffre correspondant.
  // Le prompt l'interdit déjà au modèle depuis mai, mais l'instruction ne suffit pas :
  // un O est passé en relecture le 22/07. On corrige en aval plutôt que d'espérer.
  // O et Q se confondent tous deux avec le zéro, I avec le un.
  // Garde-fou : la substitution n'est retenue QUE si elle rend valide un VIN qui ne
  // l'était pas. Sur une valeur tronquée, bruitée ou déjà correcte, on ne touche à rien.
  // Sur les 13 238 VIN en base, 20 portent une de ces lettres et tous sont des lectures
  // fautives d'un VIN réel (WFOAXX… pour un Ford WF0, WOLBD6… pour un Opel W0L).
  if (values.vin) {
    const vinBrut = String(values.vin).toUpperCase();
    const vinCorrige = vinBrut.replace(/[OQ]/g, '0').replace(/I/g, '1');
    values.vin = !isVinFormatValid(vinBrut) && isVinFormatValid(vinCorrige) ? vinCorrige : vinBrut;
  }

  // Téléphone : l'indicatif international arrive sous des formes variées (+33, 0033,
  // 033 collé au numéro). Toutes doivent redonner un numéro national à 10 chiffres,
  // sinon MISIV reçoit des numéros comme « 0337723816740 ».
  // Retourne '' quand le numéro ne correspond à aucun format français connu : l'appelant
  // garde alors la valeur d'origine. On ne réécrit que ce qu'on a su interpréter, sinon
  // un numéro étranger ou bruité ressortirait déformé sans que personne ne le voie.
  const normalizeTelephone = (raw: unknown): string => {
    const chiffres = String(raw ?? '').replace(/\D/g, '');
    if (!chiffres) return '';
    // 0033XXXXXXXXX, puis 033XXXXXXXXX, puis 33XXXXXXXXX : on retire l'indicatif et on
    // repose le 0 national. L'ordre compte, le plus long préfixe d'abord.
    for (const prefixe of ['0033', '033', '33']) {
      if (chiffres.startsWith(prefixe) && chiffres.length - prefixe.length === 9) {
        return `0${chiffres.slice(prefixe.length)}`;
      }
    }
    // Numéro national déjà correct : on le rend sans séparateurs, format attendu en base.
    if (/^0[1-9]\d{8}$/.test(chiffres)) return chiffres;
    return '';
  };
  // `buyer_telephone` est le seul champ téléphone du formulaire (cf. documentFieldSources).
  if (values.buyer_telephone) {
    const normalise = normalizeTelephone(values.buyer_telephone);
    if (normalise) values.buyer_telephone = normalise;
  }

  // Civilité : l'OCR retourne "M." / "Mme" / "Monsieur" / "Madame" mais les
  // RadioGroup attendent "mr" / "mme". On normalise.
  const normalizeCivilite = (raw: unknown): string => {
    const s = String(raw ?? '')
      .trim()
      .toLowerCase()
      .replace(/\./g, '');
    if (s === 'm' || s === 'mr' || s === 'monsieur') return 'mr';
    if (s === 'mme' || s === 'madame') return 'mme';
    return '';
  };
  if (values.seller_civilite) values.seller_civilite = normalizeCivilite(values.seller_civilite);
  if (values.buyer_civilite) values.buyer_civilite = normalizeCivilite(values.buyer_civilite);
  if (values.cobuyer_civilite) values.cobuyer_civilite = normalizeCivilite(values.cobuyer_civilite);

  // Type de client : inféré depuis les données OCR (SIRET/SIREN ou raison sociale = pro)
  const sellerIsPro = Boolean(values.seller_siret || values.seller_siren || values.seller_raison_sociale);
  values.seller_client_type = sellerIsPro ? 'pro' : 'person';

  const hasBuyerSiret = Boolean(values.buyer_siret);
  values.buyer_client_type = hasBuyerSiret ? 'pro' : 'person';

  // buyer_residence_type : pas d'inférence OCR possible, toujours 'owner' par défaut
  if (!values.buyer_residence_type) values.buyer_residence_type = 'owner';

  // cobuyer_client_type : pas d'inférence, 'person' par défaut
  if (!values.cobuyer_client_type) values.cobuyer_client_type = 'person';

  return { values, confidences };
};
